import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";
import Reveal from "../../../../components/LandingPage/v2/Reveal";
import {
	SectionV2,
	InnerV2,
	HeadV2,
	GlassCard,
} from "../../../../components/LandingPage/v2/primitives";

const easeOut = (t) => 1 - (1 - t) ** 3;

/**
 * Animates the numeric core of headline values like "+100M", "2 meses" or
 * "+75% NPS" once the card scrolls into view; prefix/suffix stay static.
 */
const CountUp = ({ value }) => {
	const match = value.match(/^([^\d]*)(\d+)(.*)$/);
	const target = match ? parseInt(match[2], 10) : 0;
	const ref = useRef(null);
	const [n, setN] = useState(0);

	useEffect(() => {
		if (!match) return undefined;
		const reduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (reduced || typeof IntersectionObserver === "undefined") {
			setN(target);
			return undefined;
		}
		let rafId = null;
		const io = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;
				io.disconnect();
				const t0 = performance.now();
				const tick = (now) => {
					const t = Math.min(1, (now - t0) / 1400);
					setN(Math.round(easeOut(t) * target));
					if (t < 1) rafId = requestAnimationFrame(tick);
				};
				rafId = requestAnimationFrame(tick);
			},
			{ threshold: 0.4 },
		);
		io.observe(ref.current);
		return () => {
			io.disconnect();
			if (rafId) cancelAnimationFrame(rafId);
		};
		// value is static per card
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!match) return <span>{value}</span>;
	return (
		<span ref={ref}>
			{match[1]}
			{n}
			{match[3]}
		</span>
	);
};

const Metrics = ({ divisionId, infos, selectedLanguage, theme }) => {
	const { t } = useTranslation();

	const title = t("businessDivisionV2.metricsTitle");
	const titleWords = title.split(" ");
	const lastWord = titleWords.pop();

	return (
		<SectionV2 $accent={theme.accent} $glowAt="80% 0%">
			<InnerV2>
				<Reveal>
					<HeadV2 $accent={theme.accent}>
						<span className="overline">
							{t("businessDivisionV2.metricsOverline")} ·{" "}
							{t(`businessDivision.${divisionId}`)}
						</span>
						<h2>
							{titleWords.join(" ")} <em>{lastWord}</em>
						</h2>
					</HeadV2>
				</Reveal>

				<Grid>
					{infos?.map((info, i) => (
						<Reveal key={i} delay={i * 120} y={30}>
							<Card $accent={theme.accent}>
								<h3>
									<CountUp value={info.title[selectedLanguage]} />
								</h3>
								<p>{info.content[selectedLanguage]}</p>
							</Card>
						</Reveal>
					))}
				</Grid>
			</InnerV2>
		</SectionV2>
	);
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
  }

  /* Reveal wrappers must stretch so all cards share a height */
  > div {
    display: flex;

    > div {
      flex: 1;
    }
  }
`;

const Card = styled(GlassCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* cards share a height via the Grid stretch; center the value+label as a
     block so shorter cards don't leave their content floating at the top */
  justify-content: center;
  text-align: center;
  padding: clamp(30px, 4vw, 46px) 26px;

  h3 {
    font-family: "Hind Madurai";
    font-size: clamp(2.6rem, 4.4vw, 3.6rem);
    font-weight: 700;
    line-height: 1;
    letter-spacing: -1px;
    color: ${(p) => p.$accent};
    text-shadow: 0 0 36px ${(p) => p.$accent + "59"};
    margin: 0 0 14px 0;
    font-variant-numeric: tabular-nums;

    /* globals.scss "* { color }" hits the CountUp span directly, which
       would beat the inherited accent — pin it back */
    span {
      color: inherit;
    }
  }

  p {
    font-family: "Hind Madurai";
    font-size: 0.98rem;
    line-height: 1.65;
    color: #a9afb8;
    max-width: 260px;
    margin: 0;
  }
`;

export default Metrics;
